import React, { useState, useEffect } from "react";
import { Card, Upload, Modal, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import LogoService from "../../../Services/Manage/LogoService";
import { ENDPOINT } from '../../../Services/ENDPOINT';

function ManageOrganization() {
  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const normalizeUrl = (url) => {
    const baseUrl = ENDPOINT.base.replace(/\/+$/, '');
    const path = url.replace(/^\/+/, '');
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}/${path}`;
    return fullUrl.replace(/([^:]\/)\/+/g, "$1");
  };

  useEffect(() => {
    async function fetchLogos() {
      try {
        const response = await LogoService.getAllLogos();
        if (response.success && response.data.length > 0) {
          const logo = response.data[0];
          const filename = logo.image_path.split('/').pop();
          setFileList([
            {
              uid: String(logo.id),
              dbId: logo.id,
              name: filename,
              status: "done",
              url: normalizeUrl(logo.image_path),
              originFileObj: null,
            },
          ]);

        }
      } catch (error) {
        message.error("Failed to load logos");
      }
    }
    fetchLogos();
  }, []);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || (file.url && file.url.substring(file.url.lastIndexOf("/") + 1)));
  };

  const handleChange = async ({ file, fileList: newFileList }) => {
    if (file.status === "uploading") {
      setFileList(newFileList);
    }

    if (file.status === "done" || file.originFileObj) {
      try {
        const uploadedFile = file.originFileObj || file;
        let response;
        const dbId = file.dbId;

        if (dbId && file.originFileObj) {
          response = await LogoService.updateLogo(dbId, uploadedFile);
        } else {
          response = await LogoService.createLogo(uploadedFile);
        }

        if (response.success) {
          message.success("Logo uploaded successfully");
          const logoData = response.data;
          const filename = logoData.image_path.split('/').pop();

          setFileList([
            {
              uid: String(logoData.id),
              dbId: logoData.id,
              name: filename,
              status: "done",
              url: normalizeUrl(logoData.image_path),
              originFileObj: null,
            },
          ]);
        } else {
          message.error("Failed to upload logo");
        }
      } catch (error) {
        message.error("Error uploading logo");
      }
    }
  };



  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <Card title="Upload Logo" >
      <Upload
        listType="picture-card"
        fileList={fileList}
        onPreview={handlePreview}
        accept="image/jpeg,image/jpg,image/png,image/webp"
        maxCount={1}
        showUploadList={{
          showPreviewIcon: true,
          showRemoveIcon: true,
        }}
        customRequest={async ({ file, onSuccess, onError }) => {
          try {
            let response;
            if (file.dbId) {
              response = await LogoService.updateLogo(file.dbId, file);
            } else {
              response = await LogoService.createLogo(file);
            }

            if (response.success) {
              message.success("Logo uploaded successfully");
              const logoData = response.data;
              const filename = logoData.image_path.split('/').pop();

              setFileList([
                {
                  uid: String(logoData.id),
                  dbId: logoData.id,
                  name: filename,
                  status: "done",
                  url: normalizeUrl(logoData.image_path),
                  originFileObj: null,
                },
              ]);

              onSuccess("ok");
            } else {
              message.error("Failed to upload logo");
              onError(new Error("Upload failed"));
            }
          } catch (error) {
            message.error("Error uploading logo");
            onError(error);
          }
        }}
        onChange={({ fileList: newFileList }) => {
          const updatedFileList = newFileList.map(file => {
            const existingFile = fileList.find(f => f.uid === file.uid);
            return {
              ...file,
              dbId: existingFile?.dbId || file.dbId,
            };
          });
          setFileList(updatedFileList);
        }}

        onRemove={async (file) => {
          try {
            console.log("Removing file:", file);

            if (file.dbId) {

              await LogoService.deleteLogoById(file.dbId);
              message.success("Logo deleted from database");
            } else if (file.name) {

              await LogoService.deleteLogoImageByFilename(file.name);
              message.success("Logo image deleted from file system");
            } else {
              message.warning("Unable to delete. No ID or filename found.");
            }

            setFileList([]);
          } catch (error) {
            message.error("Error deleting logo");
          }
        }}

      >
        {fileList.length >= 1 ? null : uploadButton}
      </Upload>


      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img alt="preview" style={{ width: "100%" }} src={previewImage} />
      </Modal>
    </Card>
  );
}

export default ManageOrganization;
